import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import {
  Controller,
  SubmitErrorHandler,
  useFormContext,
} from 'react-hook-form';
import { $convertToMarkdownString } from '@lexical/markdown';
import {
  StorefrontFormData,
  ABOUT_MAX_LENGTH,
  ROLE_TITLE_MAX_LENGTH,
  CTA_FORM_LABEL_MAX_LENGTH,
  CTA_BUTTON_TEXT_MAX_LENGTH,
  TAGLINE_MAX_LENGTH,
} from '.';
import { useAuth } from '../../../contexts/AuthContext';
import { useUpdateProviderStorefrontMutation } from '../../../generated/graphql';
import EditControls from '../../components/EditControls';
import Editor from '../../components/Editor';
import InputGroup from '../../components/InputGroup';
import PractitionerBanner from '../../components/Storefront/PractitionerBanner';
import PractitionerInfoContainer from '../../components/Storefront/PractitionerInfoContainer';
import PractitionerInfoLeftColumn from '../../components/Storefront/PractitionerInfoLeftColumn';
import PractitionerInfoRightColumn, {
  DEFAULT_TAGLINE,
  DEFAULT_ABOUT,
} from '../../components/Storefront/PractitionerInfoRightColumn';
import { MARKDOWN_TRANSFORMERS } from '../../components/Editor/plugins/MarkdownTransformers';
import { resizeTextArea } from '../../lib/form';

const InformationModule: React.FC<{
  onSubmit: (data: StorefrontFormData) => Promise<boolean>;
  onErrors: SubmitErrorHandler<StorefrontFormData>;
  profileImageUrl?: string;
  bannerImageUrl?: string;
  bannerPosition?: number;
  onMediaChange?: () => Promise<void>;
  initialAbout?: string;
  className?: string;
}> = ({
  onSubmit,
  onErrors,
  profileImageUrl,
  bannerImageUrl,
  bannerPosition,
  onMediaChange,
  initialAbout,
  className,
}) => {
  const { authedProviderUser, refreshAuthedProviderUser } = useAuth();
  // Use account owner, because the storefront is, for now at least, only made for the account owner
  // not for each individual provider user of the account.
  const providerName = authedProviderUser?.provider?.name;
  const providerProfileImageUrl =
    profileImageUrl ??
    authedProviderUser?.provider?.ownerProfileImageMedia?.url;

  const { control, register, handleSubmit, watch, resetField } =
    useFormContext<StorefrontFormData>();

  const watchRoleTitle = watch('roleTitle');
  const watchCtaFormLabel = watch('ctaFormLabel');
  const watchCtaButtonText = watch('ctaButtonText');
  const watchTagline = watch('tagline');
  const watchAbout = watch('about');

  const servicesFieldRef = useRef<HTMLTextAreaElement | null>(null);
  const [aboutEditorKey, setAboutEditorKey] = useState(0);

  const [inEditMode, setInEditMode] = useState(false);

  const handleSaveChanges = () => {
    handleSubmit(async (data) => {
      const success = await onSubmit(data);
      if (success) {
        setInEditMode(false);
        await refreshAuthedProviderUser();
      }
    }, onErrors)();
  };

  const handleDiscardChanges = () => {
    resetField('roleTitle');
    resetField('ctaFormLabel');
    resetField('ctaButtonText');
    resetField('tagline');
    resetField('about');
    resetField('services');
    setAboutEditorKey((k) => k + 1);
    setInEditMode(false);
  };

  const [updateProviderStorefront] = useUpdateProviderStorefrontMutation();

  const onBannerPositionChange = async (bannerPosition: number) => {
    try {
      await updateProviderStorefront({
        variables: {
          input: {
            bannerPosition: Math.round(bannerPosition),
          },
        },
      });
      await onMediaChange();
    } catch (error) {
      console.error('Failed to update banner position', error);
    }
  };

  return (
    <div
      className={classNames('relative overflow-hidden rounded-b-xl', className)}
    >
      <PractitionerBanner
        editable
        bannerImageUrl={bannerImageUrl}
        initialBannerPosition={bannerPosition}
        avatarImageUrl={providerProfileImageUrl}
        onMediaChange={onMediaChange}
        onBannerPositionChange={onBannerPositionChange}
        avatarName={providerName}
      />
      {/* Main area */}
      <PractitionerInfoContainer>
        <EditControls
          isEditing={inEditMode}
          onEditClick={() => {
            setInEditMode(true);
            setTimeout(() => {
              resizeTextArea(servicesFieldRef);
            }, 0);
          }}
          handleSaveChanges={handleSaveChanges}
          handleDiscardChanges={handleDiscardChanges}
          useAbsolutePosition
        />

        {/* Left column */}
        <PractitionerInfoLeftColumn
          providerName={providerName}
          signUpEnabled={false}
          roleTitle={watchRoleTitle}
          ctaFormLabel={watchCtaFormLabel}
          ctaButtonText={watchCtaButtonText}
          inEditMode={inEditMode}
          editModeTitleSpecializationChildren={
            <InputGroup
              label="Title & Specialization"
              labelHidden
              placeholder="Title & Specialization"
              className="border-0 text-center focus:border-0 focus:outline-none"
              backgroundHidden
              maxLength={ROLE_TITLE_MAX_LENGTH}
              characterCounter
              maxLengthValue={ROLE_TITLE_MAX_LENGTH}
              currentLengthValue={watchRoleTitle?.length}
              {...register('roleTitle', { maxLength: ROLE_TITLE_MAX_LENGTH })}
            />
          }
          editModeCTAFormLabelChildren={
            <InputGroup
              label="Form label"
              labelHidden
              placeholder="Form label"
              className="border-0 text-center font-medium focus:border-0 focus:outline-none"
              backgroundHidden
              maxLength={CTA_FORM_LABEL_MAX_LENGTH}
              characterCounter
              maxLengthValue={CTA_FORM_LABEL_MAX_LENGTH}
              currentLengthValue={watchCtaFormLabel?.length}
              {...register('ctaFormLabel', {
                maxLength: CTA_FORM_LABEL_MAX_LENGTH,
              })}
            />
          }
          editModeCTAButtonTextChildren={
            <InputGroup
              label="Button text"
              labelHidden
              placeholder="Button text"
              containerClassName="mt-4"
              className="h-[44.5px] rounded-full border border-neutral-75 text-center font-medium focus:border-neutral-75 focus:outline-none"
              backgroundHidden
              maxLength={CTA_BUTTON_TEXT_MAX_LENGTH}
              characterCounter
              maxLengthValue={CTA_BUTTON_TEXT_MAX_LENGTH}
              currentLengthValue={watchCtaButtonText?.length}
              {...register('ctaButtonText', {
                maxLength: CTA_BUTTON_TEXT_MAX_LENGTH,
              })}
            />
          }
        />
        {/* Right column */}
        <PractitionerInfoRightColumn
          tagline={watchTagline}
          about={watchAbout}
          inEditMode={inEditMode}
          editModeTaglineChildren={
            <InputGroup
              label="Tagline"
              labelHidden
              placeholder={DEFAULT_TAGLINE}
              className={classNames(
                'w-full border-0 px-0 py-0 font-serif text-title-small font-light',
                'placeholder:font-sans placeholder:text-category',
                'focus:border-0 focus:outline-none',
              )}
              backgroundHidden
              maxLength={TAGLINE_MAX_LENGTH}
              characterCounter
              maxLengthValue={TAGLINE_MAX_LENGTH}
              currentLengthValue={watchTagline?.length}
              {...register('tagline', { maxLength: TAGLINE_MAX_LENGTH })}
            />
          }
          editModeAboutChildren={
            <div className="flex w-full min-w-0 flex-col">
              <Controller
                name="about"
                control={control}
                rules={{
                  maxLength: {
                    value: ABOUT_MAX_LENGTH,
                    message: `About section cannot exceed ${ABOUT_MAX_LENGTH} characters.`,
                  },
                }}
                render={({ field }) => (
                  <Editor
                    key={`about-${aboutEditorKey}`}
                    initialContentMarkdown={field.value ?? initialAbout ?? ''}
                    elKey={`about-${aboutEditorKey}`}
                    placeholder={DEFAULT_ABOUT}
                    className="min-w-0 overflow-hidden text-neutral-150"
                    contentClassName="!text-caption min-h-[80px] break-words overflow-x-hidden [overflow-wrap:anywhere]"
                    containerClassName="w-full min-w-0 max-w-full overflow-hidden border-0"
                    onChange={(editorState) => {
                      editorState.read(() => {
                        const markdown = $convertToMarkdownString(
                          MARKDOWN_TRANSFORMERS,
                          undefined,
                          true,
                        );
                        field.onChange(markdown);
                      });
                    }}
                  />
                )}
              />
              {watchAbout?.length > ABOUT_MAX_LENGTH - 100 && (
                <div
                  className={classNames(
                    'mt-1 text-right text-small-caption',
                    watchAbout?.length > ABOUT_MAX_LENGTH
                      ? 'text-red-125'
                      : 'text-neutral-125',
                  )}
                >
                  {watchAbout?.length}/{ABOUT_MAX_LENGTH}
                  <span className="ml-1 text-neutral-100">
                    (formatting counts toward limit)
                  </span>
                </div>
              )}
            </div>
          }
        />
      </PractitionerInfoContainer>
    </div>
  );
};

export default InformationModule;
